import { AppEventEmitter } from './AppEventEmitter';

export const runHelper = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  getArgs: () => Args | undefined,
  clearArgs: () => void,
): void => {
  const args = getArgs();
  if (args) {
    fn(...args);
    clearArgs();
  }
};

const debounce = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  delay: number,
): ((...args: Args) => void) => {
  let timeoutID: number | undefined;
  let lastArgs: Args | undefined;

  const debounced = (...args: Args): void => {
    clearTimeout(timeoutID);
    lastArgs = args;
    timeoutID = window.setTimeout(() => {
      runHelper(
        fn,
        () => lastArgs,
        () => {
          lastArgs = undefined;
        },
      );
    }, delay);
  };

  debounced.flush = () => {
    clearTimeout(timeoutID);
    runHelper(
      fn,
      () => lastArgs,
      () => {
        lastArgs = undefined;
      },
    );
  };

  return debounced;
};

const throttle = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  cooldown: number,
): ((...args: Args) => void) => {
  let lastArgs: Args | undefined;

  const throttled = (...args: Args): void => {
    const isOnCooldown = !!lastArgs;

    lastArgs = args;

    if (isOnCooldown) {
      return;
    }

    window.setTimeout(() => {
      runHelper(
        fn,
        () => lastArgs,
        () => {
          lastArgs = undefined;
        },
      );
    }, cooldown);
  };

  return throttled;
};

export const EventsUtils = {
  AppEventEmitter,
  throttle,
  debounce,
};
